import React from 'react';
import { Coffee, Utensils } from 'lucide-react';

const DietPreferencesSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  dietTypes 
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Utensils className="w-5 h-5 mr-2 text-blue-500" />
        Предпочтения в питании
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип диеты */}
        <div className="col-span-full">
          <label htmlFor="dietType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип питания
          </label>
          <select
            id="dietType"
            name="dietType"
            value={formData.dietType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.dietType ? 'input-error' : ''}`}
          >
            <option value="" disabled>Выберите тип питания</option>
            {dietTypes.map((diet) => (
              <option key={diet.id} value={diet.id}>
                {diet.name}
              </option>
            ))}
          </select>
          {errors.dietType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.dietType}
            </p>
          )}
          {formData.dietType && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {dietTypes.find(d => d.id === formData.dietType)?.description || ''}
            </p>
          )}
        </div>

        {/* Количество приемов пищи */}
        <div>
          <label htmlFor="mealsPerDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Coffee className="w-4 h-4 mr-2 text-blue-500" />
            Количество приемов пищи
          </label>
          <select
            id="mealsPerDay"
            name="mealsPerDay"
            value={formData.mealsPerDay}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.mealsPerDay ? 'input-error' : ''}`}
          >
            <option value="1">1 прием пищи</option>
            <option value="2">2 приема пищи</option>
            <option value="3">3 приема пищи</option>
            <option value="4">4 приема пищи</option>
            <option value="5">5 приемов пищи</option>
            <option value="6">6 приемов пищи</option>
          </select>
          {errors.mealsPerDay && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.mealsPerDay}
            </p>
          )}
        </div>

        {/* Включать ли перекусы */}
        <div className="flex items-center">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includeSnacks"
              checked={formData.includeSnacks}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Включать перекусы между основными приемами пищи
            </span>
          </label>
        </div>
      </div>

      {/* Макронутриентный баланс визуализация */}
      {formData.dietType && (
        <div className="mt-5">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Распределение макронутриентов для выбранного типа питания:
          </h4>
          
          <div className="grid grid-cols-3 gap-2">
            {/* Белки */}
            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                {Math.round(dietTypes.find(d => d.id === formData.dietType)?.macros.protein * 100)}%
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">Белки</div>
            </div>
            
            {/* Жиры */}
            <div className="bg-amber-100 dark:bg-amber-900/30 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                {Math.round(dietTypes.find(d => d.id === formData.dietType)?.macros.fat * 100)}%
              </div>
              <div className="text-xs text-amber-700 dark:text-amber-300">Жиры</div>
            </div>
            
            {/* Углеводы */}
            <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-green-600 dark:text-green-400">
                {Math.round(dietTypes.find(d => d.id === formData.dietType)?.macros.carbs * 100)}%
              </div>
              <div className="text-xs text-green-700 dark:text-green-300">Углеводы</div>
            </div>
          </div>
          
          {/* Визуальное отображение пропорций */}
          <div className="w-full h-6 bg-gray-200 dark:bg-gray-700 rounded-full mt-4 overflow-hidden flex">
            <div 
              className="h-full bg-blue-500" 
              style={{ width: `${dietTypes.find(d => d.id === formData.dietType)?.macros.protein * 100}%` }}
              title="Белки"
            ></div>
            <div 
              className="h-full bg-amber-500" 
              style={{ width: `${dietTypes.find(d => d.id === formData.dietType)?.macros.fat * 100}%` }}
              title="Жиры"
            ></div>
            <div 
              className="h-full bg-green-500" 
              style={{ width: `${dietTypes.find(d => d.id === formData.dietType)?.macros.carbs * 100}%` }}
              title="Углеводы"
            ></div>
          </div>
        </div>
      )}
      
      {/* Дополнительная информация о соотношении макронутриентов */}
      <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <Utensils className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              О распределении макронутриентов
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Белки (4 ккал/г) важны для восстановления мышц и поддержания чувства сытости.
              Жиры (9 ккал/г) необходимы для гормонального баланса и усвоения витаминов.
              Углеводы (4 ккал/г) являются основным источником энергии для организма.
              Оптимальное соотношение зависит от ваших целей и индивидуальных особенностей.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietPreferencesSection;